<script setup>
import { computed } from 'vue';

const props = defineProps({
  from: { type: Number, default: 0 },
  to: { type: Number, default: 0 },
  total: { type: Number, default: 0 },
  // How many rows the viewer has ticked, shown alongside the range so a
  // selection spanning pages is not invisible once they turn one.
  selected: { type: Number, default: 0 },
});

const emit = defineEmits(['previous', 'next']);

const atStart = computed(() => props.from <= 1);
const atEnd = computed(() => props.to >= props.total);
const empty = computed(() => props.total === 0);
</script>

<template>
  <div class="xl-pagination">
    <span v-if="props.selected" class="xl-pagination__selected">{{ props.selected }} selected</span>

    <span class="xl-pagination__range">
      <template v-if="empty">Nothing to show</template>
      <template v-else>{{ props.from }}–{{ props.to }} of {{ props.total }}</template>
    </span>

    <button
      type="button"
      class="xl-pagination__step"
      aria-label="Previous page"
      :disabled="atStart || empty"
      @click="emit('previous')"
    >‹</button>
    <button
      type="button"
      class="xl-pagination__step"
      aria-label="Next page"
      :disabled="atEnd || empty"
      @click="emit('next')"
    >›</button>
  </div>
</template>

<style scoped>
.xl-pagination, .xl-pagination * { box-sizing: border-box; }

.xl-pagination {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px 6px 16px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-pill);
  background: #fff;
  box-shadow: var(--xl-shadow-1);
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-primary-text);
}

.xl-pagination__selected {
  padding-right: 10px;
  border-right: 1px solid var(--xl-color-border);
  color: var(--xl-color-primary);
  font-weight: 600;
}

.xl-pagination__range { font-variant-numeric: tabular-nums; }

.xl-pagination__step {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--xl-color-secondary-text);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.xl-pagination__step:hover:not(:disabled) {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
}

.xl-pagination__step:disabled { color: var(--xl-color-outline-variant); cursor: not-allowed; }

.xl-pagination__step:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 1px; }
</style>
